import Ajv from 'ajv';
import * as React from 'react';
import {
  VoiceboxResponseAction,
  VoiceboxResponseActionType,
} from 'vet-bones/constants/voicebox';
import {
  AllModelingAndMapping,
  MODELING_AND_MAPPING_SCHEMA,
  ModelingAndMappingAction,
  migrate,
} from 'vet-bones/utils/voicebox';

import { useClickModelConcept } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConcept';
import { useClearDiagram } from 'src/containers/ProjectDiagram/hooks/useClearDiagram';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import { applyVoiceboxChanges } from 'src/containers/ProjectDiagram/utils/applyVoiceboxChanges';
import { clearDiagramUndoHistory } from 'src/containers/ProjectDiagram/utils/clearDiagramUndoHistory';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { createDraftData } from 'src/containers/ProjectDiagram/utils/draft/createDraftData';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import { projectTargetModelIdSelector } from 'src/selectors/projectTargetModelId';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const ajv = new Ajv();
const validator = ajv.compile(MODELING_AND_MAPPING_SCHEMA);

export const useVoiceboxModelingAndMapping = () => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const clearDiagram = useClearDiagram();
  const dispatch = useReduxDispatch();
  const handleClickIri = useClickModelConcept();
  const targetModelId = useReduxSelector(
    projectTargetModelIdSelector(projectId)
  );

  return React.useCallback(
    async (actions: VoiceboxResponseAction[]) => {
      if (!diagram) {
        return;
      }

      let didError = false;
      const validActions: ModelingAndMappingAction[] = [];
      actions.forEach((action) => {
        if (action.type !== VoiceboxResponseActionType.MODELING_AND_MAPPING) {
          return;
        }

        try {
          const result = JSON.parse(action.value || '{}');
          const isValid = validator(result);
          if (!isValid) {
            throw new Error(JSON.stringify(validator.errors, null, 2));
          }
          const { actions } = migrate(result as AllModelingAndMapping);
          validActions.push(...actions);
        } catch (err) {
          console.error(err);
          didError = true;
        }
      });

      if (didError) {
        dispatch(
          uiSlice.actions.voiceboxModelingAndMappingFailed({
            message: 'Problem applying changes from Voicebox',
          })
        );
        return;
      }

      if (!validActions.length) {
        console.warn('No valid modeling and mapping changes to apply');
        return;
      }

      clearDiagram();

      if (!diagram.model.modelData.draftData) {
        // clear undo history for new draft
        clearDiagramUndoHistory(diagram, projectId);
      }
      commitDiagramModel(diagram, (model) => {
        // setup draft data
        if (!model.modelData.draftData) {
          const draftData = createDraftData(model);
          setDataProperty<ModelData>(model)(
            model.modelData,
            'draftData',
            draftData
          );
        }
      });

      const addedOrUpdatedIris = new Set<string>();
      const removedIris = new Set<string>();

      const didCommit = await commitDiagramModel(
        diagram,
        applyVoiceboxChanges(
          validActions,
          targetModelId ? [targetModelId] : [],
          addedOrUpdatedIris,
          removedIris
        ),
        { layout: DiagramModelCommitLayout.PARTIAL }
      );

      if (!didCommit) {
        return;
      }

      // Canvas re-render + relayout are diagram-only.
      if (!diagram.headless) {
        diagram.updateAllTargetBindings();
        diagram.layout.doLayout(diagram);
      }

      if (removedIris.size) {
        dispatch(
          uiSlice.actions.removedKeysFromEntityDrawerHistory({
            projectId,
            entityValues: Array.from(removedIris),
          })
        );
      }

      if (addedOrUpdatedIris.size === 1) {
        handleClickIri(Array.from(addedOrUpdatedIris)[0]);
      }
    },
    [clearDiagram, diagram, dispatch, handleClickIri, projectId, targetModelId]
  );
};
